import dayjs from 'dayjs';

import type { LensCardData, LensFilterValues } from '~/types/leLens';

import { csvCell } from '~/utils/csvCell';

export type Reading = Record<string, string | number | null>;
export type ReportConfig = {
  tablePageSize?: Record<string, number>;
  transformRequest?: (request: LensFilterValues, now?: Date) => LensFilterValues;
  transformCard?: (card: LensCardData) => LensCardData;
  pageId: string;
  title: string;
  source: string;
  date: string;
  filters: Array<string>;
  defaultModel?: string;
  parameters?: Record<string, string>;
  viewFilters?: Record<string, Array<string>>;
  splitDimensions?: Record<string, Array<string>>;
  derived?: Record<string, Array<[number, string]>>;
  views: Array<{ id: string; label: string; card: string; dimension: string; series?: string; global?: boolean }>;
  definitions: Record<string, string>;
  primary: Array<string>;
  ratios: Record<string, [string, string]>;
  limitations: Array<string>;
};
export function numeric(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(typeof value === 'object' && 'value' in value ? value.value : value);
  return Number.isFinite(number) ? number : null;
}
export function reading(value: unknown): string | number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'object' && 'value' in value) return reading(value.value);
  return typeof value === 'number' ? value : String(value);
}
export function dateError(from: string, to: string): string | null {
  const valid = (s: string) =>
    /^\d{4}-\d{2}-\d{2}$/.test(s) && dayjs(s).isValid() && dayjs(s).format('YYYY-MM-DD') === s;
  return !valid(from) || !valid(to) || from > to ? 'Choose valid dates with From on or before To.' : null;
}
/** Resolve the report's civil calendar once from an injectable instant, not browser locale. */
export function reportCalendar(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Australia/Sydney',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const today = ['year', 'month', 'day'].map((key) => parts.find((part) => part.type === key)!.value).join('-');
  return { today, zone: 'Australia/Sydney' as const };
}
export function defaultDates(now = new Date()) {
  const { today } = reportCalendar(now);
  return {
    from: dayjs(today).subtract(28, 'day').format('YYYY-MM-DD'),
    to: dayjs(today).subtract(1, 'day').format('YYYY-MM-DD'),
  };
}
export function reportFilters(
  from: string,
  to: string,
  selections: Record<string, Array<string>>,
  comparison: string,
): LensFilterValues {
  return {
    ...Object.fromEntries(Object.entries(selections).filter(([, v]) => v.length)),
    period: {
      window: { from, to },
      comparison: comparison === 'none' ? null : comparison === 'previous' ? 'previous' : { back: 1, unit: 'year' },
    },
  };
}
export function rowsFor(
  card: LensCardData | undefined,
  ratios: ReportConfig['ratios'],
  prior = false,
  derived: NonNullable<ReportConfig['derived']> = {},
): Array<Reading> {
  const rows = prior ? card?.comparison?.rows : card?.rows;
  return (rows ?? []).map((row) => {
    const result: Reading = Object.fromEntries(Object.entries(row).map(([key, value]) => [key, reading(value)]));
    for (const [key, operands] of Object.entries(derived)) {
      if (!operands.some(([, operand]) => operand in result)) continue;
      const values = operands.map(([sign, operand]) => {
        const value = numeric(result[operand]);
        return value === null ? null : sign * value;
      });
      result[key] = values.some((value) => value === null)
        ? null
        : values.reduce<number>((sum, value) => sum + (value ?? 0), 0);
    }
    for (const [key, [n, d]] of Object.entries(ratios)) {
      if (!(n in result) && !(d in result)) continue;
      const num = numeric(result[n]),
        den = numeric(result[d]);
      result[key] = num === null || den === null || den === 0 ? null : num / den;
    }
    return result;
  });
}
export function csvFor(
  config: ReportConfig,
  range: string,
  selections: Record<string, Array<string>>,
  rows: Array<Reading>,
): string {
  const cell = (v: unknown) => csvCell(v, 'Unknown');
  const keys = [...new Set(rows.flatMap((row) => Object.keys(row)))];
  return (
    '\ufeff' +
    [
      ['Report', config.title],
      ['Source', config.source],
      ['Period', range],
      ['Filters', JSON.stringify(selections)],
      ['Ratios', 'Decimal ratios of aggregate components; no daily averages. AUD monetary values.'],
      keys,
      ...rows.map((row) => keys.map((key) => row[key])),
    ]
      .map((row) => row.map(cell).join(','))
      .join('\r\n')
  );
}
export function download(name: string, contents: string) {
  const url = URL.createObjectURL(new Blob([contents], { type: 'text/csv;charset=utf-8;' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}
export function label(key: string) {
  const names: Record<string, string> = {
    gm: 'GM (BR)',
    gm_ar: 'GM (AR)',
    ttv: 'TTV (BR)',
    ttv_ar: 'TTV (AR)',
    cost: 'Costs (AUD)',
    sign_ups: 'Website sign-ups',
    gm_br: 'GM (BR)',
    sales_gm_ar: 'GM (AR)',
    ttv_br: 'TTV (BR)',
    sales_ttv_ar: 'TTV (AR)',
    orders_br: 'Orders (BR)',
    customers_br: 'Customers (BR)',
    margin_aud_ar: 'GM (AR)',
    margin_aud_br: 'GM (BR)',
    gross_sales_aud_ar: 'TTV (AR)',
    gross_sales_aud_br: 'TTV (BR)',
    cost_aud: 'Marketing spend (AUD)',
    margin_roas: 'ROAS (GM BR)',
    margin_roas_ar: 'ROAS (GM AR)',
    session_conversion: 'Orders / sessions',
    cpc: 'CPC (AUD)',
    ctr: 'CTR',
    net_cm: 'Net CM incl. finance',
    comparison_cm: 'Comparison Net CM',
  };
  if (names[key]) return names[key];
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bGm\b/g, 'GM')
    .replace(/\bTtv\b/g, 'TTV')
    .replace(/\bAud\b/g, 'AUD')
    .replace(/\bRoas\b/g, 'ROAS')
    .replace(/\bCm\b/g, 'CM');
}

/** Project additive month-to-date components, then recompute ratios of the projected sums. */
export function monthExpectation(
  rows: Array<Reading>,
  from: string,
  to: string,
  ratios: ReportConfig['ratios'],
): Array<Reading> {
  if (from.slice(0, 7) !== to.slice(0, 7) || !from.endsWith('-01')) return [];
  const factor = dayjs(to).daysInMonth() / dayjs(to).date();
  const groups = new Map<string, Reading>();
  for (const row of rows) {
    const region = String(row.region ?? 'All markets');
    const next = groups.get(region) ?? { region };
    for (const [key, value] of Object.entries(row)) {
      if (typeof value === 'number' && !ratios[key]) next[key] = (numeric(next[key]) ?? 0) + value * factor;
    }
    groups.set(region, next);
  }
  return rowsFor({ cardId: 'projection', status: 'ready', rows: [...groups.values()] }, ratios);
}

/** Join periods on actual dimension values; never average ratios or sum repeated model spend. */
export function compareReadings(
  current: Array<Reading>,
  prior: Array<Reading>,
  dimensions: Array<string>,
  absoluteBase: boolean,
): Array<Reading> {
  const key = (row: Reading) => JSON.stringify(dimensions.map((dimension) => row[dimension] ?? null));
  const present = new Map(current.map((row) => [key(row), row]));
  const previous = new Map(prior.map((row) => [key(row), row]));
  const groups = [...new Set([...present.keys(), ...previous.keys()])];
  const metricKeys = [
    ...new Set(
      [...current, ...prior].flatMap((row) =>
        Object.keys(row).filter((metric) => !dimensions.includes(metric) && typeof row[metric] === 'number'),
      ),
    ),
  ];
  return groups.map((group) => {
    const row = present.get(group);
    const old = previous.get(group);
    const result: Reading = row
      ? { ...row }
      : Object.fromEntries(dimensions.map((dimension) => [dimension, old?.[dimension] ?? null]));
    for (const metric of metricKeys) {
      const value = numeric(row?.[metric]);
      const base = numeric(old?.[metric]);
      result[metric] = value;
      result[`${metric}_previous`] = base;
      result[`${metric}_growth`] =
        value === null || base === null || base === 0
          ? null
          : absoluteBase
            ? (value - base) / Math.abs(base)
            : value / base - 1;
    }
    return result;
  });
}

export function reportQueryChanges(params: URLSearchParams, changes: Record<string, unknown>): URLSearchParams {
  const next = new URLSearchParams(params);
  if (!next.has('reportView') && next.has('view')) next.set('reportView', next.get('view')!);
  next.delete('view');
  for (const [key, value] of Object.entries(changes)) {
    next.delete(key);
    if (Array.isArray(value)) {
      for (const item of value) next.append(key, String(item));
      if (!value.length) next.set(key, '');
    } else if (value !== null && value !== undefined) next.set(key, String(value));
  }
  return next;
}

/** Bound the rendered table only; callers keep the complete rows for totals and CSV. */
export function visibleTableRows<T>(rows: Array<T>, size: number | undefined, page: number) {
  const pageSize = size && Number.isSafeInteger(size) && size > 0 ? size : rows.length || 1;
  const currentPage = Math.max(0, Math.min(page, Math.ceil(rows.length / pageSize) - 1));
  return {
    count: rows.length,
    page: currentPage,
    pageSize,
    rows: size ? rows.slice(currentPage * pageSize, (currentPage + 1) * pageSize) : rows,
  };
}

/** Preserve the chart's first matching mark while avoiding a full scan for each plotted point. */
export function indexPlotRows(rows: Array<Reading>, dimension: string, series?: string) {
  const marks = new Map<string, Map<string, Reading>>();
  const totals = new Map<string, Map<string, number>>();
  for (const row of rows) {
    const date = String(row[dimension]),
      name = String(series ? (row[series] ?? 'Unknown') : 'Selected scope');
    let values = marks.get(date);
    if (!values) {
      values = new Map();
      marks.set(date, values);
    }
    if (!values.has(name)) values.set(name, row);
  }
  return {
    get: (date: string, name: string) => marks.get(date)?.get(name),
    total: (metric: string, date: string) => {
      let values = totals.get(metric);
      if (!values) {
        values = new Map();
        for (const row of rows) {
          const key = String(row[dimension]);
          values.set(key, (values.get(key) ?? 0) + (numeric(row[metric]) ?? 0));
        }
        totals.set(metric, values);
      }
      return values.get(date) ?? 0;
    },
  };
}
